import { HEN_BREEDS, BREED_IDS } from '../data/breeds';
import { STARTER_BOXES } from '../data/balance';
import type { BreedId } from '../data/types';
import type { Flock, GameMessage, GameState, Hen, NestBox, SimCtx } from './types';

export const SAVE_VERSION = 1;

export const FLOCK_BACKGROUNDS = ['meadow', 'forest', 'ember', 'stormy', 'copper', 'plum', 'sandy', 'midnight', 'cherry'] as const;

/** First egg lands ~2 s after placement for a starter hen. */
export const FIRST_EGG_PROGRESS = 0.7;

export function makeHen(breed: BreedId, wall: number, variant: number): Hen {
  return {
    breed,
    variant,
    placedWall: wall,
    progress: FIRST_EGG_PROGRESS,
    broodySince: null,
    layingUntil: null,
    traits: [],
    broods: 0,
  };
}

export function makeBox(hen: Hen | null = null): NestBox {
  return { hen, eggs: 0, capacityLevel: 0, oldestEggAt: null };
}

export function makeFlock(id: number): Flock {
  return {
    id,
    name: `Flock ${id}`,
    nameIsCustom: false,
    rooster: null,
    boxes: [],
    coop: [],
    crowBuffs: [],
    background: id === 1 ? 'theme' : FLOCK_BACKGROUNDS[(id - 2) % FLOCK_BACKGROUNDS.length]!,
    bloomNextAt: null,
  };
}

export interface NewGameOptions {
  seed: number;
  wall: number;
  farmName?: string;
}

export function createGame({ seed, wall, farmName = '' }: NewGameOptions): GameState {
  const flock = makeFlock(1);
  const starters: BreedId[] = ['RIR', 'LEGHORN'];
  for (let i = 0; i < STARTER_BOXES; i++) {
    flock.boxes.push(makeBox(makeHen(starters[i] ?? 'RIR', wall, i % 3)));
  }
  return {
    version: SAVE_VERSION,
    seed,
    rng: seed >>> 0,
    rev: 0,
    time: 0,
    wallAt: wall,
    createdWall: wall,
    runStartWall: wall,
    sessionStartWall: wall,
    farmName,
    money: 0,
    lifetimeEarned: 0,
    eggsCollected: 0,
    eggsLaid: 0,
    flocks: [flock],
    viewFlock: 1,
    unlockedBreeds: BREED_IDS.filter((b) => HEN_BREEDS[b].unlock.kind === 'start'),
    newBreeds: [],
    upgrades: [],
    gather: {},
    infra: { scratchFeeder: 0, waterer: 0, eggInspector: 0, warmingLamp: 0 },
    vouchers: 0,
    roosterBreedsEver: [],
    usedRoosterNames: [],
    buffs: { lay: [], click: [], auto: [], gather: [] },
    clutch: [],
    debuffUntil: 0,
    combo: { tier: 0, clicks: [], highSince: 0, lowSince: 0 },
    events: [],
    critters: [],
    nextId: 1,
    timers: { lastAnySpawnAt: 0, firstEventDone: false, stuckSince: 0, stuckHens: 0, stuckBurstAt: -Infinity, critterNext: {}, stormWaveAt: 0 },
    stats: {
      clicks: 0, crows: 0, goldenClicked: 0, silverClicked: 0, rainbowClicked: 0, broodyHatches: 0,
      rottenSeen: 0, rottenSalvaged: 0, rottenExpired: 0, roosterEggsHatched: 0, eggsLost: 0,
      activePlaySec: 0, lifetimeActivePlaySec: 0, eggBuckets: [], eggBucketSec: 0, roosterWalls: [], peakIncome: 0,
    },
    achievements: [],
    challengeTiers: {},
    won: false,
    wonWall: null,
    acceleratorBank: 0,
    activity: [],
  };
}

// ── Small shared helpers ──────────────────────────────────────────────

export function nextId(s: GameState): number {
  return s.nextId++;
}

export function countHens(s: GameState): number {
  let n = 0;
  for (const f of s.flocks) for (const b of f.boxes) if (b.hen) n++;
  return n;
}

export function countRoosters(s: GameState): number {
  return s.flocks.reduce((n, f) => n + (f.rooster ? 1 : 0), 0);
}

export function countBoxes(s: GameState): number {
  return s.flocks.reduce((n, f) => n + f.boxes.length, 0);
}

export function findFlock(s: GameState, flockId: number): Flock | undefined {
  return s.flocks.find((f) => f.id === flockId);
}

export function flockIndex(s: GameState, flockId: number): number {
  return Math.max(0, s.flocks.findIndex((f) => f.id === flockId));
}

export function ownedBreedCount(s: GameState, breed: BreedId): number {
  let n = 0;
  for (const f of s.flocks) for (const b of f.boxes) if (b.hen?.breed === breed) n++;
  return n;
}

export function ownedBreeds(s: GameState): Set<BreedId> {
  const set = new Set<BreedId>();
  for (const f of s.flocks) for (const b of f.boxes) if (b.hen) set.add(b.hen.breed);
  return set;
}

export function ctxAt(wall: number): SimCtx {
  return { wall, localHour: new Date(wall).getHours() };
}

/** Marks cached modifiers stale. Call after any change to hens, roosters, purchases or traits. */
export function touch(s: GameState): void {
  s.rev++;
}

// ── Outbox: messages for the UI, kept out of the serializable state ────

const outboxes = new WeakMap<GameState, GameMessage[]>();

export function emit(s: GameState, msg: GameMessage): void {
  let box = outboxes.get(s);
  if (!box) {
    box = [];
    outboxes.set(s, box);
  }
  box.push(msg);
  if (box.length > 200) box.splice(0, box.length - 200);
}

export function drainMessages(s: GameState): GameMessage[] {
  const box = outboxes.get(s);
  if (!box || box.length === 0) return [];
  outboxes.set(s, []);
  return box;
}

export const ACTIVITY_CAP = 200;

export function logActivity(s: GameState, kind: GameState['activity'][number]['kind'], title: string, detail: string): void {
  s.activity.push({ wall: s.wallAt, kind, title, detail });
  if (s.activity.length > ACTIVITY_CAP) s.activity.splice(0, s.activity.length - ACTIVITY_CAP);
}
